import { Injectable, inject, signal } from '@angular/core';
import { Observable, tap, catchError, throwError, from, switchMap, delay } from 'rxjs';
import { UserService } from '../services/user.service';
import { AppUser } from '../models/appUser';
import { UserSectionAccess } from '../models/userSectionAccess';
import { AppUserCompanyAccessType } from '../models/enums/appUserCompanyAccessType.enum';
import { Helpers } from '../utilities/helpers';
import { DEVELOPER_REALM_ROLE } from '../utilities/auth.constants';
import Keycloak from 'keycloak-js';

@Injectable({
  providedIn: 'root'
})
export class CurrentUserState {
  private userService = inject(UserService);
  private keycloak = inject(Keycloak);

  // Signals to store current user and permissions
  private currentUser = signal<AppUser | null>(null);
  private sectionAccess = signal<UserSectionAccess[]>([]);
  private companyAccessTypes = signal<AppUserCompanyAccessType[]>([]);
  private companyAccessReadOnlyTypes = signal<AppUserCompanyAccessType[]>([]);
  private isLoaded = signal<boolean>(false);

  // Public readonly signals
  readonly user = this.currentUser.asReadonly();
  readonly permissions = this.sectionAccess.asReadonly();
  readonly companyAccess = this.companyAccessTypes.asReadonly();
  readonly companyAccessReadOnly = this.companyAccessReadOnlyTypes.asReadonly();
  readonly loaded = this.isLoaded.asReadonly();

  /**
   * Load current user data from API
   * Called by APP_INITIALIZER before app starts
   * Waits for Keycloak to be ready before making the API call
   */
  loadCurrentUser(): Observable<AppUser> {
    // Poll for Keycloak token to be available (with timeout)
    const waitForToken = (attempts = 0, maxAttempts = 50): Promise<string> => {
      return new Promise((resolve, reject) => {
        if (this.keycloak.token) {
          resolve(this.keycloak.token);
        } else if (attempts >= maxAttempts) {
          reject(new Error('Timeout waiting for Keycloak token'));
        } else {
          setTimeout(() => {
            waitForToken(attempts + 1, maxAttempts).then(resolve).catch(reject);
          }, 100); // Check every 100ms
        }
      });
    };

    return from(waitForToken()).pipe(
      switchMap(token => this.userService.getCurrentUser()),
      tap(user => {
        this.currentUser.set(user);
        this.sectionAccess.set(user.sectionAccess || []);
        this.companyAccessTypes.set(user.companyAccess || []);
        this.companyAccessReadOnlyTypes.set(user.companyAccessReadOnly || []);
        this.isLoaded.set(true);
      }),
      catchError(error => {
        console.error('Error loading current user:', error);
        this.isLoaded.set(true); // Mark as loaded even on error
        return throwError(() => error);
      })
    );
  }

  /**
   * Check if user has access to a specific section/subsection
   */
  hasAccess(section: string, subSection: string): boolean {
    return this.sectionAccess().some(
      access =>
        access.section === section &&
        access.subSection === subSection &&
        access.visible === true
    );
  }

  /**
   * Check if user can create in a specific section/subsection
   */
  canCreate(section: string, subSection: string): boolean {
    const access = this.sectionAccess().find(
      a => a.section === section && a.subSection === subSection
    );
    return access?.canCreate ?? false;
  }

  /**
   * Check if user can edit in a specific section/subsection
   */
  canEdit(section: string, subSection: string): boolean {
    const access = this.sectionAccess().find(
      a => a.section === section && a.subSection === subSection
    );
    return access?.canEdit ?? false;
  }

  /**
   * Check if user can delete in a specific section/subsection
   */
  canDelete(section: string, subSection: string): boolean {
    const access = this.sectionAccess().find(
      a => a.section === section && a.subSection === subSection
    );
    return access?.canDelete ?? false;
  }

  /**
   * Check if user has access to a specific company tab
   */
  hasCompanyAccess(accessType: AppUserCompanyAccessType): boolean {
    return this.companyAccessTypes().includes(accessType);
  }

  /**
   * Whether the current user holds a company tab read-only — the tab renders, but every control
   * in it is disabled and the backend rejects writes to it.
   */
  isCompanyTabReadOnly(accessType: AppUserCompanyAccessType): boolean {
    return this.companyAccessReadOnlyTypes().includes(accessType);
  }

  /**
   * Whether the current user may modify a company tab: they hold it, and not read-only.
   */
  canEditCompanyTab(accessType: AppUserCompanyAccessType): boolean {
    return this.hasCompanyAccess(accessType) && !this.isCompanyTabReadOnly(accessType);
  }

  /**
   * Whether the current user holds the Keycloak `developer` realm role. This is independent of the
   * DB-based AppUserRole business roles and gates developer-only views such as the audit log.
   */
  isDeveloper(): boolean {
    return this.keycloak.hasRealmRole(DEVELOPER_REALM_ROLE);
  }

  /**
   * Get all sections user has access to
   */
  getAccessibleSections(): UserSectionAccess[] {
    return this.sectionAccess().filter(access => access.visible);
  }

  /**
   * Returns the default measurement year (previous calendar year),
   * constrained by the user's accessible years if applicable.
   */
  defaultMeasurementYear(): string {
    const defaultYear = Number(Helpers.defaultMeasurementYear());
    const accessToYears = this.currentUser()?.accessToYears;
    if (accessToYears && accessToYears.length > 0) {
      if (accessToYears.includes(defaultYear)) {
        return defaultYear.toString();
      }
      const pastYears = accessToYears.filter(y => y <= defaultYear);
      if (pastYears.length > 0) {
        return Math.max(...pastYears).toString();
      }
    }
    return Helpers.defaultMeasurementYear();
  }

  /**
   * Returns measurement years filtered by user access.
   */
  accessibleMeasurements(): string[] {
    const all = Helpers.measurements();
    const accessToYears = this.currentUser()?.accessToYears;
    if (!accessToYears || accessToYears.length === 0) return all;
    const yearSet = new Set(accessToYears.map(y => y.toString()));
    return all.filter(m => yearSet.has(m));
  }

  /**
   * Clear user data (e.g., on logout)
   */
  clear(): void {
    this.currentUser.set(null);
    this.sectionAccess.set([]);
    this.companyAccessTypes.set([]);
    this.isLoaded.set(false);
  }
}
